import React from 'react'
import { Divider, Stack, Typography } from '@mui/material'
import DocLink from '../../components/DocLink'
import { Bullet, Bullets, Info, Step, Warning } from '../../components/DocsComponents'

const RecruitmentApplications = () => (
  <Stack gap={3} divider={<Divider />}>
    <Stack gap={1}>
      <Typography variant="title3">How recruitment works in AmbuNet</Typography>
      <Typography>
        AmbuNet gives your organisation its own online application form. People
        who want to join you create an account on your AmbuNet site, complete a
        step-by-step application (personal details, right to work, employment
        history, qualifications, DBS, driving and references), and submit it.
        Your Personnel team then reviews the application, collects references,
        holds interviews, makes an offer, and — once accepted — turns the
        application into an employee record without re-typing anything.
      </Typography>
      <Typography>
        There is one rolling application route for your whole organisation
        rather than a separate advert per vacancy. Applicants choose the
        clinical role they are applying for inside the form, and you can limit
        which roles are open at any time.
      </Typography>
      <Info title="Who can do this?">
        Employee applications need a <strong>Premium</strong> (tier 2) or
        higher subscription. Applications are reviewed by the{' '}
        <strong>Personnel</strong> team from{' '}
        <DocLink text="Employees > Applications" url="/employees/applications" />
        , and the application settings are in{' '}
        <DocLink
          text="Site Configuration > Employees > Applications"
          url="/site-configuration/applications"
        />{' '}
        (site administrators and HR). Site administrators always have access.
      </Info>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Before you start: application settings</Typography>
      <Typography>
        Open{' '}
        <DocLink
          text="Site Configuration > Employees > Applications"
          url="/site-configuration/applications"
        />{' '}
        and check these settings, then choose <strong>Save Changes</strong>:
      </Typography>
      <Bullets>
        <Bullet>
          <strong>Enable new employee applications</strong> — turns your
          application form on or off. When it is off, new visitors see an{' '}
          <strong>Applications closed</strong> message, but anyone who has
          already started an application can still finish and submit it.
        </Bullet>
        <Bullet>
          <strong>Restrict Applications By Role</strong> — only accept
          applications for the roles you pick. The sign-up page tells visitors
          which roles are open, and the role list in the application form is
          limited to them. Leave it empty to accept every role.
        </Bullet>
        <Bullet>
          <strong>Automatically send reference request emails</strong> — when
          on, both referees are emailed as soon as the applicant submits. When
          off, you send reference requests yourself from the application.
        </Bullet>
        <Bullet>
          <strong>Remind applicants to finish draft applications</strong> —
          applicants who haven&apos;t touched an unsubmitted application for 48
          hours get a reminder email, every 48 hours, up to 10 reminders.
        </Bullet>
        <Bullet>
          <strong>Onboarding Checklist</strong> — the tasks (for example
          &quot;Issue uniform&quot; or &quot;Complete induction&quot;) added to
          every new starter&apos;s checklist when you accept their application.
        </Bullet>
      </Bullets>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Where to send people who want to apply</Typography>
      <Typography>
        At the top of{' '}
        <DocLink
          text="Site Configuration > Employees > Applications"
          url="/site-configuration/applications"
        />{' '}
        you&apos;ll find <strong>Your application link</strong>. This is the
        address to give anyone who wants to work for you. It takes them straight
        to your <strong>Join the team</strong> sign-up page.
      </Typography>
      <Bullets>
        <Bullet>
          <strong>Copy link</strong> — paste it into the careers or
          &quot;work for us&quot; page on your website, job adverts, social
          media posts, recruitment emails or text messages.
        </Bullet>
        <Bullet>
          <strong>Download QR code</strong> — a printable QR code for posters,
          leaflets, recruitment fairs and training days. Scanning it opens the
          same sign-up page.
        </Bullet>
      </Bullets>
      <Typography>
        The link looks like{' '}
        <strong>https://yourorganisation.ambunet.co.uk/register?accountType=applicant</strong>
        , using your own AmbuNet address. People who find your AmbuNet sign-in
        page themselves can also choose <strong>Register</strong> and then{' '}
        <strong>Employee / Applicant</strong> to reach the same form.
      </Typography>
      <Info title="Point people back to the same link">
        Applicants use the same address to come back later: the sign-up page has
        an <strong>Already started your application?</strong> section with a{' '}
        <strong>Continue</strong> button that takes them to sign in. There is no
        need to send a different link to people part-way through.
      </Info>
      <Warning title="When the link won't work">
        The link only accepts new applicants while{' '}
        <strong>Enable new employee applications</strong> is switched on and your
        organisation is on Premium or above. If either changes, visitors are told
        that applications are closed — so check the setting before you publish
        an advert.
      </Warning>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">What the applicant does</Typography>
      <Step number={1} title="Create an account">
        On the <strong>Join the team</strong> page they enter their name, email
        address and a password, then choose <strong>Start Application</strong>.
      </Step>
      <Step number={2} title="Verify their email">
        They are sent a <strong>Verify your Email Address</strong> email. The
        email comes from no-reply@ambunet.co.uk (ask them to check their spam
        folder) and the link is valid for 24 hours. If they sign in before
        verifying, they are offered <strong>Re-Send Verification Email</strong>.
      </Step>
      <Step number={3} title="Complete the application form">
        After signing in they see their <strong>Job application</strong> rather
        than the normal staff area. The form is split into steps: Personal
        details, Address (five years of address history, needed for DBS checks),
        Right to work, Employment history (with any gaps explained), Supporting
        statement, Qualifications (including the role they are applying for),
        DBS check, Driving, References (two referees) and Review &amp; submit.
        Progress saves as they move between steps, and{' '}
        <strong>Save &amp; sign out</strong> lets them come back later.
      </Step>
      <Step number={4} title="Submit and track progress">
        Once every required step is complete they submit. They then see a
        progress tracker showing references received, interviews, any offer, and
        the final decision, with <strong>Accept Offer</strong> and{' '}
        <strong>Decline Offer</strong> buttons when you make an offer.
      </Step>
      <Info>
        Applicants never see your suitability notes or internal interview notes.
      </Info>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Reviewing applications</Typography>
      <Typography>
        <DocLink text="Employees > Applications" url="/employees/applications" />{' '}
        lists applications in three tabs: <strong>Active</strong> (drafts and
        submitted applications), <strong>Approved</strong> and{' '}
        <strong>Rejected</strong>. Each row shows the applicant&apos;s role,
        their references, the next interview, and a stage chip. The stage
        updates automatically based on what has happened:
      </Typography>
      <Bullets>
        <Bullet><strong>Draft</strong> — still being filled in by the applicant.</Bullet>
        <Bullet><strong>Submitted</strong> — submitted and awaiting review.</Bullet>
        <Bullet><strong>Awaiting References</strong> — reference requests sent, waiting on referees.</Bullet>
        <Bullet><strong>References Received</strong> — both references are in.</Bullet>
        <Bullet><strong>Interview Scheduled</strong> — an interview is booked.</Bullet>
        <Bullet><strong>Interview Completed</strong> — record an outcome or make an offer.</Bullet>
        <Bullet><strong>Offer Extended</strong> — waiting for the applicant to respond.</Bullet>
        <Bullet><strong>Offer Accepted</strong> / <strong>Offer Declined</strong> — the applicant&apos;s answer.</Bullet>
        <Bullet><strong>Approved</strong> / <strong>Rejected</strong> — the final decision.</Bullet>
      </Bullets>
      <Typography>
        Open an application to see everything the applicant provided, organised
        into the same sections as their form, with summary tiles for
        references, interviews, employment history (including gaps) and
        driving. Use <strong>Suitability Notes</strong> to record your
        assessment — these are only visible to your team.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">References</Typography>
      <Typography>
        If automatic references are on, both referees are emailed a{' '}
        <strong>Reference Request</strong> when the application is submitted.
        Referees complete the reference online without needing an account. From
        the <strong>Options</strong> menu next to each referee you can:
      </Typography>
      <Bullets>
        <Bullet>
          <strong>Resend Invitation</strong> — send (or re-send) the request
          email.
        </Bullet>
        <Bullet>
          <strong>Show Referee URL</strong> — get the referee&apos;s personal link
          and <strong>Copy to Clipboard</strong>, for example to send it from
          your own email.
        </Bullet>
        <Bullet>
          <strong>Edit Referee</strong> — correct the referee&apos;s details.
        </Bullet>
        <Bullet>
          <strong>Add Response</strong> — record a reference taken by phone or
          received on paper.
        </Bullet>
      </Bullets>
      <Info>
        Referee links stay valid for 90 days. References are confidential: they
        remain on the application and are not copied onto the employee record.
      </Info>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Interviews</Typography>
      <Typography>
        Choose <strong>Schedule Interview</strong> on the application to book
        one, with a title, start and finish time, location, details for the
        applicant and the interviewers. Tick{' '}
        <strong>Email the applicant an interview invitation</strong> to send them
        the details. If you later change the date or time, they can be emailed
        the update.
      </Typography>
      <Typography>
        After the interview, record who was present, your interview notes
        (internal only) and an outcome: <strong>Pending</strong>,{' '}
        <strong>Passed</strong>, <strong>Failed</strong>,{' '}
        <strong>No Show</strong> or <strong>Cancelled</strong>.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Offers and decisions</Typography>
      <Typography>
        These actions are on the application page and are available once the
        application has been submitted:
      </Typography>
      <Bullets>
        <Bullet>
          <strong>Extend Offer</strong> — emails the applicant an offer of
          employment, with an optional message. They accept or decline from
          their application tracker. If they decline, you can{' '}
          <strong>Re-extend Offer</strong>.
        </Bullet>
        <Bullet>
          <strong>Accept Application</strong> — creates their employee record.
          You must choose at least one base location, and your plan&apos;s
          employee limit is checked first.
        </Bullet>
        <Bullet>
          <strong>Reject Application</strong> — emails the applicant that they
          have not been successful, including your reason if you give one. This
          can also be used on draft applications.
        </Bullet>
      </Bullets>
      <Warning title="Rejecting removes the applicant's login">
        Rejecting an application deletes the applicant&apos;s AmbuNet account.
        The application itself stays in the <strong>Rejected</strong> tab for
        your records. If they want to apply again later, they register again
        using your application link.
      </Warning>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Onboarding the new starter</Typography>
      <Typography>
        When you accept an application, AmbuNet creates the employee record and
        opens it for you. It sets their status to <strong>New Starter</strong>{' '}
        and carries across:
      </Typography>
      <Bullets>
        <Bullet>Their personal and contact details and clinical role.</Bullet>
        <Bullet>
          Uploaded documents — right to work, driving, qualifications, DBS
          certificate and mandatory training — attached to their record.
        </Bullet>
        <Bullet>
          A DBS check record from the certificate they declared. It does not
          count as valid until someone adds the completed date or the DBS Update
          Service confirms it.
        </Bullet>
        <Bullet>Driving entitlements (such as C1 and blue light).</Bullet>
        <Bullet>The base locations you picked.</Bullet>
        <Bullet>
          Your <strong>Onboarding Checklist</strong> tasks, shown on their
          employee page to tick off as they are completed.
        </Bullet>
      </Bullets>
      <Typography>
        Employment history, supporting statement and references are not copied
        onto the employee record — they remain on the approved application. The
        applicant receives a <strong>Welcome</strong> email and can sign in to
        the full staff area straight away. From here, follow{' '}
        <DocLink docsPage url="/employees-hr/adding-employees" text="Adding Employees" />{' '}
        to finish their profile, and set up{' '}
        <DocLink
          docsPage
          url="/employees-hr/mandatory-training-and-e-learning"
          text="Mandatory Training"
        />{' '}
        and{' '}
        <DocLink
          docsPage
          url="/employees-hr/occupational-health"
          text="Occupational Health"
        />{' '}
        as needed.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Emails applicants receive</Typography>
      <Bullets>
        <Bullet>
          <strong>Verify your Email Address</strong> — when they create their
          account.
        </Bullet>
        <Bullet>
          <strong>Your application is not yet submitted</strong> — draft
          reminders, if switched on.
        </Bullet>
        <Bullet>
          <strong>Interview Invitation</strong> /{' '}
          <strong>Interview Updated</strong> — when you book or move an
          interview with the email option ticked.
        </Bullet>
        <Bullet>
          <strong>Offer of Employment</strong> — when you extend an offer.
        </Bullet>
        <Bullet>
          <strong>Application Updated</strong> — when an application is
          rejected.
        </Bullet>
        <Bullet>
          <strong>Welcome</strong> — when their application is accepted.
        </Bullet>
      </Bullets>
      <Typography>
        Referees receive a separate <strong>Reference Request</strong> email.
      </Typography>
    </Stack>
  </Stack>
)

export default RecruitmentApplications
